import { useState, useEffect, useRef } from 'react';

interface ExtractedColors {
  dominant: string;
  vibrant: string;
  muted: string;
}

// Pure black default - prevents grey flash on iOS Safari glass sampling
// before the actual color is extracted from the image
const defaultColors: ExtractedColors = {
  dominant: 'rgb(0, 0, 0)',
  vibrant: 'rgb(30, 30, 30)',
  muted: 'rgb(20, 20, 20)',
};

// Cache for extracted colors - exported so it can be pre-warmed
export const colorCache = new Map<string, ExtractedColors>();

// Helper to get cached color synchronously (for initial render)
export function getCachedColor(imageUrl: string | undefined | null): ExtractedColors | null {
  if (!imageUrl) return null;
  return colorCache.get(imageUrl) || null;
}

// Pre-warm the cache so first render (and iOS Safari glass sampling) uses real colors.
// Returns cached colors if already present.
export async function prewarmColorExtraction(
  imageUrl: string | undefined | null,
): Promise<ExtractedColors | null> {
  if (!imageUrl) return null;

  const cached = colorCache.get(imageUrl);
  if (cached) return cached;

  return await new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const sampleSize = 50;
        canvas.width = sampleSize;
        canvas.height = sampleSize;
        ctx.drawImage(img, 0, 0, sampleSize, sampleSize);

        const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
        const data = imageData.data;
        const colorSamples: { r: number; g: number; b: number; saturation: number }[] = [];

        for (let i = 0; i < data.length; i += 16) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          if (a < 128) continue;

          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          colorSamples.push({ r, g, b, saturation });
        }

        if (colorSamples.length === 0) {
          resolve(null);
          return;
        }

        const dominantSample = colorSamples[Math.floor(colorSamples.length / 3)];
        const vibrantSample = colorSamples.reduce((prev, curr) =>
          curr.saturation > prev.saturation ? curr : prev,
        );
        const mutedSample = colorSamples.reduce((prev, curr) => {
          const currBrightness = (curr.r + curr.g + curr.b) / 3;
          const prevBrightness = (prev.r + prev.g + prev.b) / 3;
          const currScore = (1 - curr.saturation) * (1 - Math.abs(currBrightness - 128) / 128);
          const prevScore = (1 - prev.saturation) * (1 - Math.abs(prevBrightness - 128) / 128);
          return currScore > prevScore ? curr : prev;
        });

        const extracted: ExtractedColors = {
          dominant: `rgb(${dominantSample.r}, ${dominantSample.g}, ${dominantSample.b})`,
          vibrant: `rgb(${vibrantSample.r}, ${vibrantSample.g}, ${vibrantSample.b})`,
          muted: `rgb(${mutedSample.r}, ${mutedSample.g}, ${mutedSample.b})`,
        };

        colorCache.set(imageUrl, extracted);
        resolve(extracted);
      } catch {
        resolve(null);
      }
    };

    img.onerror = () => resolve(null);
    img.src = imageUrl;
  });
}

export function useColorExtraction(imageUrl: string | undefined | null) {
  // Initialize with cached value if available (prevents flash on re-visits)
  const [colors, setColors] = useState<ExtractedColors>(() => {
    if (imageUrl) {
      const cached = colorCache.get(imageUrl);
      if (cached) return cached;
    }
    return defaultColors;
  });
  const [isLoading, setIsLoading] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!imageUrl) {
      setColors(defaultColors);
      return;
    }

    // Check cache first
    const cached = colorCache.get(imageUrl);
    if (cached) {
      setColors(cached);
      return;
    }

    setIsLoading(true);

    // Create canvas once
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    
    if (!ctx) {
      setIsLoading(false);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      // Use small sample for performance
      const sampleSize = 50;
      canvas.width = sampleSize;
      canvas.height = sampleSize;
      
      ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
      
      try {
        const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
        const data = imageData.data;
        
        // Sample colors from different regions
        const colorSamples: { r: number; g: number; b: number; saturation: number }[] = [];
        
        for (let i = 0; i < data.length; i += 16) { // Sample every 4th pixel
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          
          if (a < 128) continue; // Skip transparent pixels
          
          // Calculate saturation
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          
          colorSamples.push({ r, g, b, saturation });
        }
        
        if (colorSamples.length === 0) {
          setColors(defaultColors);
          setIsLoading(false);
          return;
        }
        
        // Find dominant color (most common-ish, weighted by position)
        const dominantSample = colorSamples[Math.floor(colorSamples.length / 3)];
        
        // Find vibrant color (highest saturation)
        const vibrantSample = colorSamples.reduce((prev, curr) => 
          curr.saturation > prev.saturation ? curr : prev
        );
        
        // Find muted color (low saturation, mid brightness)
        const mutedSample = colorSamples.reduce((prev, curr) => {
          const currBrightness = (curr.r + curr.g + curr.b) / 3;
          const prevBrightness = (prev.r + prev.g + prev.b) / 3;
          const currScore = (1 - curr.saturation) * (1 - Math.abs(currBrightness - 128) / 128);
          const prevScore = (1 - prev.saturation) * (1 - Math.abs(prevBrightness - 128) / 128);
          return currScore > prevScore ? curr : prev;
        });
        
        const extracted: ExtractedColors = {
          dominant: `rgb(${dominantSample.r}, ${dominantSample.g}, ${dominantSample.b})`,
          vibrant: `rgb(${vibrantSample.r}, ${vibrantSample.g}, ${vibrantSample.b})`,
          muted: `rgb(${mutedSample.r}, ${mutedSample.g}, ${mutedSample.b})`,
        };
        
        // Cache result
        colorCache.set(imageUrl, extracted);
        setColors(extracted);
      } catch (e) {
        // CORS error or other issue
        setColors(defaultColors);
      }
      
      setIsLoading(false);
    };
    
    img.onerror = () => {
      setColors(defaultColors);
      setIsLoading(false);
    };
    
    img.src = imageUrl;
  }, [imageUrl]);

  return { colors, isLoading };
}
